import { Sentry } from 'lib-shared-sentry';

import { isWebHidSupportedInBrowser } from '../../../jabra/utils/isWebHidSupportedInBrowser';

import {
  bufferPositions,
  busylightDeviceFilters,
  busylightStatus,
  tones,
} from './busylightConfigs';
import {
  BusylightEventListeners,
  BusylightEvents,
  BusylightOptions,
  BusylightSendOptions,
} from './busylightDeviceManager.types';

/** Manages Busylight devices using WebHID API. */
export class BusylightDeviceManager {
  /** Set of connected HID devices. */
  private devices = new Set<HIDDevice>();

  /** Event listeners for Busylight events. */
  private listeners: BusylightEventListeners = {
    deviceAdded: new Set(),
    deviceRemoved: new Set(),
    arrayBufferChange: new Set(),
  };

  /** Timeout identifier for device search. */
  private searchTimeoutId?: ReturnType<typeof setTimeout>;

  /** Buffer cache for the current array buffer. */
  private bufferCache?: Uint8Array;

  /** If it should keep sending the previous instruction */
  private keepAlive?: boolean;

  /** The timeout id for the keep alive timeout */
  private keepAliveTimeoutId?: ReturnType<typeof setTimeout>;

  /** Gets the current buffer cache. */
  get currentBufferCache(): BusylightDeviceManager['bufferCache'] {
    return this.bufferCache;
  }

  /** Checks if there are connected devices. */
  get hasConnectedDevices(): boolean {
    return this.devices.size > 0;
  }

  /** Gets the connected HID devices. */
  get connectedDevices(): HIDDevice[] {
    return Array.from(this.devices.values());
  }

  constructor({
    searchInterval = 20000,
    keepAlive = false,
  }: BusylightOptions = {}) {
    if (!isWebHidSupportedInBrowser()) {
      return;
    }

    this.keepAlive = keepAlive;
    this.searchForDevices(searchInterval);
  }

  /** Recursively searches for connected Busylight devices. */
  private async searchForDevices(searchInterval: number): Promise<void> {
    if (this.searchTimeoutId) clearTimeout(this.searchTimeoutId);

    await this.updateDevices();

    this.searchTimeoutId = setTimeout(() => {
      this.searchForDevices(searchInterval);
    }, searchInterval);
  }

  /** Updates the list of connected devices. */
  private async updateDevices(): Promise<void> {
    try {
      const devices = await navigator.hid.getDevices();

      const busylightDevices = devices.filter((device) =>
        busylightDeviceFilters.supportedKuandoDevices.find(
          (filter) => filter.vendorId === device.vendorId,
        ),
      );

      this.addOrRemoveDevices(busylightDevices);
    } catch (err) {
      Sentry.captureException(err);
    }
  }

  /** Triggers a request to connect Busylight devices. */
  public async triggerRequestDevice(): Promise<void> {
    try {
      const devices = await navigator.hid.requestDevice({
        filters: busylightDeviceFilters.supportedKuandoDevices,
      });

      if (!devices.length) return;

      this.addOrRemoveDevices(devices);
    } catch (err) {
      Sentry.captureException(err);
    }
  }

  /** Adds or removes devices based on the provided list. */
  private async addOrRemoveDevices(devices: HIDDevice[]): Promise<void> {
    for (const device of this.devices) {
      if (!devices.includes(device)) {
        try {
          if (device.opened) {
            await device.close();
          }
          this.removeDevice(device);
        } catch (err) {
          Sentry.captureException(err);
        }
      }
    }

    for (const device of devices) {
      if (!this.devices.has(device)) {
        try {
          if (!device.opened) {
            await device.open();
          }

          this.addDevice(device);
        } catch (err) {
          Sentry.captureException(err);
        }
      }
    }
  }

  /** Adds a device to the set of connected devices. */
  private addDevice(device: HIDDevice): void {
    this.devices.add(device);
    this.emit('deviceAdded', device);
  }

  /** Removes a device from the set of connected devices. */
  private removeDevice(device: HIDDevice): void {
    this.devices.delete(device);
    this.emit('deviceRemoved', device);
  }

  /** Creates an array buffer based on the provided options. */
  private createArrayBuffer({
    color,
    tone = 'TelephoneNordic',
    volume = 0,
    onTimeSteps = 0,
    offTimeSteps = 0,
    repeatInterval = 120,
  }: BusylightSendOptions): Uint8Array {
    const arrayBuffer = new Uint8Array(64);

    arrayBuffer[0] = 16;

    arrayBuffer.fill(0, 1); // Set all elements from index 1 to the end to zero

    arrayBuffer.fill(255, 59, 62); // Set elements from index 59 to 61 to 255

    // Color
    arrayBuffer[bufferPositions.red] = color?.r ?? 0;
    arrayBuffer[bufferPositions.green] = color?.g ?? 0;
    arrayBuffer[bufferPositions.blue] = color?.b ?? 0;

    // Sound
    arrayBuffer[bufferPositions.sound] = tones[tone] + volume;

    // On / Off time steps
    arrayBuffer[bufferPositions.onTimeSteps] = onTimeSteps;
    arrayBuffer[bufferPositions.offTimeSteps] = offTimeSteps;
    arrayBuffer[bufferPositions.repeatInterval] = repeatInterval;

    let checksum = 0;

    for (let i = 0; i < 62; i++) {
      checksum += arrayBuffer[i];
    }

    arrayBuffer[62] = checksum / 256;
    arrayBuffer[63] = checksum % 256;

    return arrayBuffer;
  }

  /** Sends a command to the connected devices. */
  public send(options: BusylightSendOptions): void {
    if (!this.devices.size) return;

    this.bufferCache = this.createArrayBuffer(options);
    this.emit('arrayBufferChange', this.bufferCache);

    this.sendToDevices(this.bufferCache);
  }

  /** Sets the status of the Busylight devices. */
  public setStatus(
    name: keyof typeof busylightStatus,
    overrides?: BusylightSendOptions,
  ): void {
    this.send({
      ...busylightStatus[name],
      ...(typeof overrides !== 'undefined' && overrides),
    });
  }

  /** Sends the array buffer to the connected devices. */
  private sendToDevices(arrayBuffer: Uint8Array): void {
    for (const device of this.devices) {
      try {
        device.sendReport(0, arrayBuffer);
      } catch (err) {
        // Don't do anything, this can happen if the devices have been removed
      }
    }
  }

  /** Updates the devices with the provided array buffer. */
  public update(arrayBuffer: Uint8Array): void {
    this.sendToDevices(arrayBuffer);

    if (this.keepAlive) {
      if (this.keepAliveTimeoutId) {
        clearTimeout(this.keepAliveTimeoutId);
      }

      this.keepAliveTimeoutId = setTimeout(() => {
        this.update(arrayBuffer);
      }, 10000);
    }
  }

  /** Emits an event with the provided arguments. */
  private emit<TKey extends keyof BusylightEvents>(
    key: TKey,
    arg: BusylightEvents[TKey],
  ): void {
    for (const handler of this.listeners[key]) {
      handler(arg);
    }
  }

  /** Adds an event handler for the specified event. */
  public on<TKey extends keyof BusylightEvents>(
    key: TKey,
    handler: (arg: BusylightEvents[TKey]) => void,
  ): void {
    this.listeners[key].add(handler);
  }

  /** Removes an event handler for the specified event. */
  public remove<TKey extends keyof BusylightEvents>(
    key: TKey,
    handler: (arg: BusylightEvents[TKey]) => void,
  ): void {
    this.listeners[key].delete(handler);
  }
}

const isSharedWorkerSupported = 'SharedWorker' in window;

export const busylightDeviceManager = new BusylightDeviceManager({
  keepAlive: !isSharedWorkerSupported,
});
